/* eslint-disable react/forbid-prop-types */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { BsArrowLeft } from 'react-icons/bs';
import { IoClose } from 'react-icons/io5';
import PropTypes from 'prop-types';
import AppButton from '../app-button/AppButton';
import { isMobileWidth } from '../../helper-utils/appUtils';

const SLIDE_ANIMATION_MS = 300;

/**
 * `isMobileWidth()` reads `window.innerWidth` once. Calling it straight from
 * render meant the drawer kept whatever layout the window had at mount time, so
 * resizing (or restoring a window that was opened narrow) left the header on the
 * wrong branch. This keeps it in sync with the actual window.
 */
function useIsMobileWidth() {
  const [mobile, setMobile] = useState(() => isMobileWidth());
  useEffect(() => {
    const onResize = () => setMobile(isMobileWidth());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

/**
 * The slide-in animation translates the dialog in from `translateX(100%)`.
 * If it ever stalls - a blocked main thread while a heavy panel (the seat map)
 * renders, a backgrounded tab, a throttled renderer - the dialog is left
 * translated to the right and the controls pinned to its right edge (the close
 * "x" and the footer "Done" button) sit outside the viewport.
 *
 * Dropping the class once the animation is done removes the transform for good,
 * and the timer is the guarantee: even if `animationend` never fires, the
 * drawer settles into its correct resting position.
 */
function useSettledAnimationClass(animatedClass) {
  const [settled, setSettled] = useState(false);
  const timerRef = useRef(null);

  const onAnimationEnd = useCallback(() => setSettled(true), []);

  useEffect(() => {
    timerRef.current = setTimeout(
      () => setSettled(true),
      SLIDE_ANIMATION_MS + 400,
    );
    return () => clearTimeout(timerRef.current);
  }, []);

  return { dialogClass: settled ? undefined : animatedClass, onAnimationEnd };
}

function AppSidebar({
  sidebarTitle,
  children,
  onHideSidebar,
  className,
  animatedClass,
  modalClassName,
  showFooter,
  footerBtnText,
  onFooterClick,
}) {
  const mobile = useIsMobileWidth();
  const { dialogClass, onAnimationEnd } =
    useSettledAnimationClass(animatedClass);

  return (
    <Modal
      backdrop="static"
      keyboard={false}
      show
      fullscreen
      onHide={onHideSidebar}
      dialogClassName={dialogClass}
      className={`app-sidebar ${modalClassName}`}
      onAnimationEnd={onAnimationEnd}
    >
      {mobile ? (
        <div className="app-sidebar__mobile-title modify-search-container p-2">
          <div className="d-flex align-items-center gap-2 px-2">
            <BsArrowLeft
              onClick={onHideSidebar}
              className="white-color cursor-pointer"
              role="button"
              aria-label="Close"
            />
            <div className="flex-1 white-color">{sidebarTitle}</div>
          </div>
        </div>
      ) : (
        <Modal.Header>
          <Modal.Title className="app-sidebar__title">
            {sidebarTitle}
          </Modal.Title>
          {/* Rendered explicitly rather than via Bootstrap's `closeButton`:
              `.btn-close` draws its glyph from a background-image data URI, so
              it renders as an invisible 32px box if that URI is ever blocked. */}
          <button
            type="button"
            className="app-sidebar__close"
            onClick={onHideSidebar}
            aria-label="Close"
          >
            <IoClose aria-hidden="true" />
          </button>
        </Modal.Header>
      )}
      <Modal.Body className={className}>{children}</Modal.Body>
      {showFooter && (
        <Modal.Footer>
          <AppButton
            btnText={footerBtnText}
            variant="outlined"
            onClick={onFooterClick}
          />
        </Modal.Footer>
      )}
    </Modal>
  );
}

AppSidebar.defaultProps = {
  className: undefined,
  animatedClass: 'slide-from-right-animation',
  modalClassName: '',
  showFooter: false,
  footerBtnText: '',
  onFooterClick: () => {},
};

AppSidebar.propTypes = {
  sidebarTitle: PropTypes.any.isRequired,
  children: PropTypes.any.isRequired,
  onHideSidebar: PropTypes.func.isRequired,
  className: PropTypes.string,
  animatedClass: PropTypes.string,
  modalClassName: PropTypes.string,
  showFooter: PropTypes.bool,
  footerBtnText: PropTypes.string,
  onFooterClick: PropTypes.func,
};

export default AppSidebar;
